"use client";
import React from "react";
import { Sun, Moon } from "lucide-react";

/**
 * ThemeToggle component allows users to switch between light and dark themes.
 * It enhances accessibility and user customization.
 */
const ThemeToggle: React.FC = () => {
  // Initialize theme state with null to avoid hydration mismatch
  const [theme, setTheme] = React.useState<"light" | "dark" | null>(null);

  const applyTheme = React.useCallback((mode: "light" | "dark") => {
    const root = document.documentElement;
    if (mode === "dark") {
      root.classList.add("dark");
      root.style.colorScheme = "dark";
    } else {
      root.classList.remove("dark");
      root.style.colorScheme = "light";
    }

    localStorage.setItem("theme", mode);
    // Set cookie immediately - use direct assignment for instant effect
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
    document.cookie = `theme=${mode}; path=/; expires=${date.toUTCString()}; SameSite=Lax`;
  }, []);

  // Initialize theme on mount before paint to avoid flicker
  React.useLayoutEffect(() => {
    const storedTheme = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)",
    ).matches;
    const initialTheme =
      storedTheme === "light" || storedTheme === "dark"
        ? (storedTheme as "light" | "dark")
        : prefersDark
          ? "dark"
          : "light";

    setTheme(initialTheme);
    applyTheme(initialTheme);
  }, [applyTheme]);

  // Update document class and persistence when theme changes
  React.useEffect(() => {
    if (theme === null) return;
    applyTheme(theme);
  }, [theme, applyTheme]);

  // Toggle theme between light and dark
  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  // Don't render until theme is initialized to avoid hydration mismatch
  if (theme === null) {
    return (
      <button
        disabled
        aria-label="Toggle theme"
        className="flex items-center gap-2 px-1 py-1 rounded-full border shadow-sm opacity-50"
      >
        <span className="relative flex items-center w-10 h-6 rounded-full bg-muted">
          <span className="absolute left-0 top-0 w-6 h-6 bg-background rounded-full shadow">
            <Moon className="w-5 h-5 m-auto text-sky-300 mt-[0.1187rem]" />
          </span>
        </span>
      </button>
    );
  }

  // Render a custom switch button
  return (
    <button
      onClick={toggleTheme}
      aria-label="Toggle theme"
      className={`flex items-center gap-2 px-1 py-1 rounded-full border shadow-sm transition
        ${
          theme === "light"
            ? "bg-muted text-foreground hover:bg-muted/80"
            : "bg-muted text-foreground hover:bg-muted/80"
        }
      `}
    >
      {/* Switch track */}
      <span
        className={`relative flex items-center w-10 h-6 rounded-full transition-colors duration-300
          ${theme === "dark" ? "bg-primary" : "bg-muted"}
        `}
      >
        {/* Switch thumb */}
        <span
          className={`absolute left-0 top-0 w-6 h-6 bg-background rounded-full shadow transform transition-transform duration-300
            ${theme === "dark" ? "translate-x-4" : "translate-x-0"}
          `}
        >
          {theme === "light" ? (
            <Sun className="w-5 h-5 m-auto text-yellow-400 mt-[0.1187rem]" />
          ) : (
            <Moon className="w-5 h-5 m-auto text-sky-300 mt-[0.1187rem]" />
          )}
        </span>
      </span>
      {/* No text label, icon only */}
    </button>
  );
};

export default ThemeToggle;
